import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  withStyles, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TableContainer, Table, TableHead, TableRow, TableBody, TableCell
} from "@material-ui/core";
import { themeTypeToBool, notArrayOrEmpty, isEmail } from '../../../../../utils/helpers';
import CancelAcceptForm from "../../../../Common/CancelAcceptForm";
import { buttonStyle } from "../buttonGroupStyle";
import { StyledChip } from '../../../../Common/CustomChip';
import ReportRow from "./ReportRow";
import t from "../../../../../languages/pl";
import guid from './../../../../../utils/guid';
import { compose } from "recompose";
import withSubordinateUser from './../../../../HOC/withSubordinateUser';
import { mapCarReportsDto } from "../../../../../requests/mapApiObject";
import MultipleElementsInput from "../../../../Common/InputFields/MultipleElementsInput";

const useStyles = theme => ({
  button: buttonStyle(theme),
  dialogActions: {
    margin: theme.spacing(2)
  },
  container: {
    margin: theme.spacing(1),
    width: "100%",
  },
  label: {
    marginLeft: theme.spacing(2),
    color: themeTypeToBool(theme.palette.type) ? "#808495" : "inherit"
  },
  chip: {
    marginLeft: theme.spacing(3)
  },
  tableContainer: {
    margin: theme.spacing(4),
    marginBottom: 0
  },
  table: {
    wordBreak: "break-word"
  },
  row: {
    overflow: "hidden"
  },
  tableCell: {
    border: 0,
    padding: 3
  }
});

const getDisplayName = (name) => {
  switch (name) {
    case "tygodniowy":
      return t.weeklyReportTitle;
    case "miesieczny":
      return t.monthlyReportTitle;
    case "kwartalny":
      return t.quarterReportTitle;
    case "opony":
      return t.aboutTireChange;
    case "ubezpieczenie":
      return t.aboutInsurance;
    case "przeglad_techniczny":
      return t.aboutTechnicalInspection;
    case "przeglad_serwis":
      return t.aboutServiceInspection;
    default:
      return name;
  }
};

const Reports = props => {
  const { classes, name, data, onDialogOpen, onSubmit, nonAdminUser } = props;
  const [open, setOpen] = useState(false);
  const [reports, setReports] = useState(data);

  useEffect(() => {
    setReports(data);
  }, [data]);

  const handleDialogOpen = () => {
    setOpen(true);
    onDialogOpen && onDialogOpen();
  };

  const handleSubmit = () => {
    onSubmit && onSubmit(mapCarReportsDto(reports));
    setOpen(false);
  };

  const handlePrivateChange = name => {
    setReports({
      ...reports,
      [name]: {
        ...reports[name],
        prywatny: !reports[name].prywatny
      }
    });
  };

  const handleDefaultChange = name => {
    setReports({
      ...reports,
      [name]: {
        ...reports[name],
        domyslny: !reports[name].domyslny
      }
    });
  };

  const printTable = data => {
    return Object.keys(data).map(key => {
      if (key.startsWith("email"))
        return;
      return (
        <ReportRow
          key={guid()}
          displayName={getDisplayName(key)}
          name={key}
          private={data[key].prywatny}
          onPrivateChange={handlePrivateChange}
          default={data[key].domyslny}
          onDefaultChange={handleDefaultChange}
        />
      );
    });
  };

  const printEmails = data => {
    if (notArrayOrEmpty(data))
      return <Typography variant="body1">{t.noDataInfo}</Typography>;
    return data.map(email => {
      return (
        <StyledChip
          key={guid()}
          label={email}
        />
      );
    });
  };

  return (
    <React.Fragment>
      <Button
        variant="outlined"
        className={classes.button}
        onClick={handleDialogOpen}
      >
        {t.reports}
      </Button>
      <Dialog
        open={open}
        fullWidth={true}
        maxWidth="md"
        onClose={() => setOpen(false)}
      >
        <DialogTitle>
          {t.reportsAndNotificationsForCar}&nbsp;{name}
        </DialogTitle>
        <DialogContent style={{ overflowX: "hidden" }}>
          <div className={classes.header}>
            <div className={classes.container}>
              <Typography
                variant="body2"
                className={classes.label}
              >
                {t.emailsAddressesFromGeneralSettings}
              </Typography>
            </div>

            {data.email_domyslny ? <div className={classes.chip}>
              {printEmails(data.email_domyslny)}
            </div> : null}

            <div className={classes.chip}>
              <MultipleElementsInput
                label={t.emailsAddressesFromPrivate}
                inputValidation={isEmail}
                elements={reports.email_prywatny || []}
                onChange={(emails) => setReports(reports => ({ ...reports, email_prywatny: emails }))}
              />
            </div>

          </div>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.row}>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle1">
                      {t.sendingReports}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle1">
                      {t.toPrivateEmailAddress}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle1">
                      {t.toGeneralEmailAddress}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {printTable(reports)}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <CancelAcceptForm onSubmit={handleSubmit} onCancel={() => setOpen(false)} disableSubmit={nonAdminUser}/>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default compose(
  withStyles(useStyles),
  withSubordinateUser
)(Reports);

Reports.propTypes = {
  classes: PropTypes.object,
  name: PropTypes.string,
  data: PropTypes.object,
  onDialogOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  //withSubordinateUser
  nonAdminUser: PropTypes.bool
};
